import { cn, Spinner, Stack } from 'component-library';
import { TFunction } from 'i18next';
import { FC, HTMLAttributes, MutableRefObject, ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { MapRef, Popup } from 'react-map-gl';

import { LandscapeType, MAP_LAYER_FACTS } from '@/api/rest/resources/types/fact';
import { LANDLER_CAT_TO_OSM_CATEGORY } from '@/api/rest/resources/types/geoserver';
import { ProtectedAreasIbat, ProtectedAreasOsm } from '@/api/rest/resources/types/protectedAreas';
import { SuspenseBoundary } from '@/components';
import { ErrorFallback } from '@/components/widgets/ErrorBoundary';
import { LAYER_OPTIONS, MAP_ORIGIN, MAP_TYPE } from '@/components/widgets/o/Map/constants';
import { INTERACTIVE_HEATMAP_LAYER_ID } from '@/components/widgets/o/Map/MapLayer';
import {
  getIucnGetLevelThreeEFGLegendValues,
  getIucnGetLevelTwoBiomeLegendValues,
} from '@/components/widgets/o/Map/utils/getLegends';
import { Logger } from '@/lib/logs/logger';
import { isNullish } from '@/utils/booleans';

import { useGetFeatureInfo } from './useGetFeatureInfo';

export const LAYERS_WITH_TOOLTIP: MAP_LAYER_FACTS[] = [
  MAP_LAYER_FACTS.combined_ibat_lulc_urls,
  MAP_LAYER_FACTS.iucn_protected_areas,
  MAP_LAYER_FACTS.opendata_protected_areas,
  MAP_LAYER_FACTS.indicative_lands,
  MAP_LAYER_FACTS.indigenous_lands,
  MAP_LAYER_FACTS.species_presence_occurrence_location_geojson,
  MAP_LAYER_FACTS.iucn_get_level_2_biome_raster_asset,
  MAP_LAYER_FACTS.iucn_get_level_3_efg_raster_asset,
];
const TOOLTIP_FEATURE_BY_LAYER: Partial<Record<MAP_LAYER_FACTS, string[]>> = {
  [MAP_LAYER_FACTS.combined_ibat_lulc_urls]: ['iucn_cat_norm', 'name', 'GRAY_INDEX'],
  [MAP_LAYER_FACTS.iucn_protected_areas]: ['iucn_cat_norm', 'name'],
  [MAP_LAYER_FACTS.opendata_protected_areas]: ['name', 'landler_class'],
  [MAP_LAYER_FACTS.indicative_lands]: ['name', 'category', 'form_rec'],
  [MAP_LAYER_FACTS.indigenous_lands]: ['identity', 'form_rec', 'category'],
  [MAP_LAYER_FACTS.species_presence_occurrence_location_geojson]: ['common_name', 'species', 'iucnRedListCategory'],
  [MAP_LAYER_FACTS.iucn_get_level_2_biome_raster_asset]: ['name', 'GRAY_INDEX'],
  [MAP_LAYER_FACTS.iucn_get_level_3_efg_raster_asset]: ['name', 'GRAY_INDEX'],
};

export type LayerTooltipProps = {
  longitude: number;
  latitude: number;
  x: number;
  y: number;
  onClose: () => void;
  layers: string[] | MAP_LAYER_FACTS[];
  layer?: string | MAP_LAYER_FACTS;
  mapRef: MutableRefObject<MapRef | null>;
  year: string;
};

export const LayerTooltip = (props: LayerTooltipProps) => {
  const showTooltip = shouldDisplayTooltip(props);
  if (!showTooltip) {
    return null;
  }

  return (
    <LayerTooltipWrapper {...props}>
      <LayerFeaturesFetcher {...props} />
    </LayerTooltipWrapper>
  );
};

const shouldDisplayTooltip = (props: LayerTooltipProps) => {
  if (!props.layers.some((layer) => LAYERS_WITH_TOOLTIP.includes(layer as MAP_LAYER_FACTS))) {
    return false;
  }

  const selectedLayers = LAYER_OPTIONS.filter((option) => props.layers.includes(option.value));

  if (!selectedLayers.length) {
    return false;
  }

  if (selectedLayers.some((layer) => layer.type !== MAP_TYPE.geojson)) {
    return true;
  }

  const map = props.mapRef.current;
  if (!map?.getLayer(INTERACTIVE_HEATMAP_LAYER_ID)) {
    return false;
  }

  return !!map.queryRenderedFeatures([props.x, props.y], {
    layers: [INTERACTIVE_HEATMAP_LAYER_ID],
  })?.length;
};

const LayerTooltipWrapper = ({
  longitude,
  latitude,
  onClose,
  children,
}: Pick<LayerTooltipProps, 'longitude' | 'latitude' | 'onClose'> & { children: ReactNode }) => {
  return (
    <Popup
      longitude={longitude}
      latitude={latitude}
      closeButton={false}
      closeOnClick={false}
      anchor='top'
      maxWidth='420px'
      offset={8}
      onClose={onClose}
    >
      <div className='rounded-xl bg-white-100 p-4'>
        <button
          onClick={onClose}
          className='absolute right-2 top-2 text-neutral-500 hover:text-neutral-700'
          aria-label='Close'
        >
          <i className='ri-close-line' />
        </button>
        <ErrorBoundary
          FallbackComponent={({ error }) => <ErrorFallback error={error} className='p-2 border-none' />}
          onError={(error, info) => {
            Logger.error(`Plot card rendering error: ${JSON.stringify(error)} ${JSON.stringify(info)}`);
          }}
        >
          <SuspenseBoundary
            fallback={
              <Stack centerMain>
                <Spinner size={10} />
              </Stack>
            }
          >
            {children}
          </SuspenseBoundary>
        </ErrorBoundary>
      </div>
    </Popup>
  );
};

const LayerFeaturesFetcher = (props: LayerTooltipProps) => {
  const selectedLayers = LAYER_OPTIONS.filter((option) => props.layers.includes(option.value));
  return (
    <Stack className='gap-2'>
      {selectedLayers.map((layer) => {
        if (layer.type === MAP_TYPE.geojson) {
          return <LayerFeaturesFetcherGeojson key={layer.value} {...props} layer={layer.value} />;
        }

        return <LayerFeaturesFetcherDefault key={layer.value} {...props} layer={layer.value} />;
      })}
    </Stack>
  );
};

const LayerFeaturesFetcherDefault = ({ longitude, latitude, layer, mapRef, year, ...props }: LayerTooltipProps) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const response = useGetFeatureInfo({ layer: layer!, longitude, latitude, mapRef, year }).data;

  const features = response?.features?.[0]?.properties;

  return <LayerTooltipBase features={features} layer={layer} {...props} />;
};

const LayerFeaturesFetcherGeojson = ({ x, y, layer, mapRef }: LayerTooltipProps) => {
  const features = mapRef.current?.getLayer(INTERACTIVE_HEATMAP_LAYER_ID)
    ? mapRef.current.queryRenderedFeatures([x, y], { layers: [INTERACTIVE_HEATMAP_LAYER_ID] })
    : undefined;

  const properties = features?.[0]?.properties as Record<string, string | number | null> | undefined;

  if (!properties) {
    return null;
  }
  return <LayerTooltipBase features={properties} layer={layer} />;
};

const LayerTooltipBase = ({
  features,
  layer,
}: Pick<LayerTooltipProps, 'layer'> & {
  features?: Record<string, string | number | null>;
}) => {
  const { t } = useTranslation();
  const intlKeys = TOOLTIP_FEATURE_BY_LAYER[layer as MAP_LAYER_FACTS] ?? [];

  const layerOrigin = LAYER_OPTIONS.find((l) => l.value === layer)?.origin;

  return (
    <Stack spacing={6} data-testid='layer-tooltip'>
      <InfoBox>
        <InfoTitle>{t(`shared.projects.project.heatMapLayers.${layer as MAP_LAYER_FACTS}.title`)}</InfoTitle>
        {features && Object.keys(features).length > 0 ? (
          intlKeys.map((key) => {
            const value = features[`${key}`];
            if (value === undefined || isNullish(value)) {
              return null;
            }

            return (
              <div key={key}>
                <InfoText>
                  {t(`shared.projects.map.layerTooltip.${key}`, {
                    defaultValue: key,
                  })}
                  {' - '}
                  {getValueText(layer as MAP_LAYER_FACTS, key, value, t)}
                </InfoText>
              </div>
            );
          })
        ) : (
          <InfoText>
            {t(
              layerOrigin !== MAP_ORIGIN.fact
                ? 'shared.projects.map.layerTooltip.noData'
                : 'shared.projects.map.layerTooltip.outsideYourArea',
            )}
          </InfoText>
        )}
      </InfoBox>
    </Stack>
  );
};

const InfoBox = ({ children }: { children: ReactNode }) => <Stack className='min-w-0 gap-2'>{children}</Stack>;

const InfoTitle: FC<HTMLAttributes<HTMLSpanElement>> = ({ className, ...props }) => (
  <span className={cn('typography-overline block text-left text-text-disabled', className)} {...props} />
);

const InfoText: FC<HTMLAttributes<HTMLSpanElement>> = ({ className, ...props }) => (
  <span className={cn('typography-body1 block text-left', className)} {...props} />
);

const getValueText = (layer: MAP_LAYER_FACTS, key: string, value: string | number, t: TFunction): string | number => {
  if (key === 'iucn_cat_norm') {
    return Object.values(ProtectedAreasIbat).includes(value as ProtectedAreasIbat) && typeof value === 'string'
      ? // eslint-disable-next-line sonarjs/no-nested-template-literals
        `${t(`global.protectedAreas.categories.${value}`, {
          defaultValue: value,
        })} - ${t(`global.protectedAreas.description.${value}`, {
          defaultValue: value,
        })}`
      : value;
  }

  if (key === 'landler_class') {
    const protectedArea =
      typeof value === 'number'
        ? (LANDLER_CAT_TO_OSM_CATEGORY?.[value as keyof typeof LANDLER_CAT_TO_OSM_CATEGORY] as
            | ProtectedAreasOsm
            | undefined)
        : undefined;

    const protectedAreaKey = Object.entries(ProtectedAreasOsm).find(([, v]) => v === protectedArea)?.[0];

    return t(`global.protectedAreas.description.${protectedAreaKey}`, {
      defaultValue: String(value),
    });
  }

  if (key === 'iucnRedListCategory') {
    return t(`global.iucn.description.${value}`, {
      defaultValue: String(value),
    });
  }

  if (key === 'GRAY_INDEX') {
    if (
      [MAP_LAYER_FACTS.iucn_get_level_2_biome_raster_asset, MAP_LAYER_FACTS.iucn_get_level_3_efg_raster_asset].includes(
        layer,
      )
    ) {
      return getEcosystemClassificationGETLabel(value as number, t);
    }
    return getLandscapeTypeLabel(value as number, t);
  }

  return value;
};

const getEcosystemClassificationGETLabel = (value: number, t: TFunction) => {
  const colors = [...getIucnGetLevelThreeEFGLegendValues(), ...getIucnGetLevelTwoBiomeLegendValues()];

  const matchedClass = colors.find((c) => c.gray_index === value);

  if (!matchedClass) {
    return t('shared.projects.map.layerTooltip.noDataTitle');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tAny = t as any;
  return tAny(matchedClass.text);
};

const getLandscapeTypeLabel = (value: number, t: TFunction) => {
  const grayIndexMap = {
    1: LandscapeType.tree_cover,
    2: LandscapeType.built_up,
    3: LandscapeType.cropland,
    4: LandscapeType.grassland,
    5: LandscapeType.bareground,
    6: LandscapeType.herbaceous_wetland,
    7: LandscapeType.shrubland,
    8: LandscapeType.permanent_water_bodies,
    9: LandscapeType.snow_and_ice,
  };

  if (value === 0) {
    return t('shared.projects.map.layerTooltip.noDataTitle');
  }

  const landType =
    typeof value === 'number'
      ? (grayIndexMap?.[value as keyof typeof grayIndexMap] as LandscapeType | undefined)
      : undefined;

  return landType
    ? t(`global.landscapeTypes.${landType}`, {
        defaultValue: landType,
      })
    : value;
};
